import React, { useState } from 'react';
import { useNotifications } from '../../hooks/useNotification';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import SpinnerComp from '../../components/SpinnerComp';
import MemberHeaders from '../../components/MemberHeader';
import EmptyScreenComp from '../../components/EmptyScreenComp';
import PaginationComp from '../../components/PaginationComp';
import ButtonComp from '../../components/ButtonComp';
import ImageModal from '../../components/ImageModal';
import NotificationItem from '../../components/NotificationItem';

export default function NotificationsPage() {
    const { notifications, markAsRead, markAllAsRead, isLoading, currentPage, totalPages, handlePageChange } = useNotifications();
    const [imageModal, setImageModal] = useState<{ isOpen: boolean; imageSrc: string; imageAlt: string }>({
        isOpen: false,
        imageSrc: '',
        imageAlt: ''
    });

    // Show all notifications instead of filtering to unread only
    const displayNotifications = notifications;

    const formatDateString = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.round((now.getTime() - date.getTime()) / 1000);
        const diffInMinutes = Math.round(diffInSeconds / 60);
        const diffInHours = Math.round(diffInMinutes / 60);
        const diffInDays = Math.round(diffInHours / 24);

        if (diffInMinutes < 1) {
            return 'just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        } else if (diffInDays < 7) {
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        } else {
            const month = date.toLocaleString('default', { month: 'short' });
            const day = date.getDate();
            return `${month} ${day}`;
        }
    };

    const handleImageClick = (imageSrc: string, imageAlt: string) => {
        setImageModal({
            isOpen: true,
            imageSrc,
            imageAlt
        });
    };

    return (
        <DashboardLayout title="Notifications" description="View your notifications">
            <div className='mb-48 md:mb-12'>
                {/* Header */}
                <MemberHeaders
                    title="Notifications"
                    showNavigation={false}
                >
                    <ButtonComp
                        onClick={markAllAsRead}
                        fullWidthOnMobile={false}
                        backgroundColor="bg-secondary-gray"
                        textColor="text-primary-blue-80"
                    >
                        Mark All as Read
                    </ButtonComp>
                </MemberHeaders>

                {/* Content Layout */}
                <div className="flex flex-col lg:flex-row lg:gap-4">
                    {/* Main Content */}
                    <div className="lg:w-full lg:min-h-screen overflow-x-auto">
                        {isLoading ? (
                            <SpinnerComp />
                        ) : (
                                <div className="border border-primary-blue-90 rounded-2xl p-4 mb-4 overflow-x-auto min-w-[800px]">
                                    {displayNotifications.length > 0 ? (
                                        displayNotifications.map((notification, index) => (
                                            <React.Fragment key={notification.id}>
                                                <NotificationItem
                                                    notification={notification}
                                                    onMarkAsRead={markAsRead}
                                                    onImageClick={handleImageClick}
                                                    formatDateString={formatDateString}
                                                />
                                                {index !== displayNotifications.length - 1 && (
                                                    <hr className='border-t border-primary-blue-90 mt-4 mb-4' />
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <EmptyScreenComp
                                            title='No Notifications Found'
                                        />
                                    )}
                            </div>
                        )}
                        {/* Pagination */}
                        {totalPages > 1 && !isLoading && (
                            <PaginationComp
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </div>
                </div>

                {/* Image Modal */}
                <ImageModal
                    isOpen={imageModal.isOpen}
                    onClose={() => setImageModal({ isOpen: false, imageSrc: '', imageAlt: '' })}
                    imageSrc={imageModal.imageSrc}
                    imageAlt={imageModal.imageAlt}
                />
            </div>
        </DashboardLayout>
    );
} 